import type {
  SelfExclusionPayload,
  TimeoutLimitsPayload,
  UserLimitPayload,
} from '@/types/UserLimits';

export const useUserLimits = () => {
  const { userLimits, userExceedTimeoutLimit, loadingUserLimits } =
    storeToRefs(useUserLimitsStore());

  const { ApiService } = useApiService();
  const { isAuthenticated } = storeToRefs(useAuthStore());
  const { getDateTime } = useDateTimeHelper();
  const { fetchUserProfile } = useUserData();

  async function setTimeOutLimit(payload: TimeoutLimitsPayload) {
    if (!payload.user_pause) {
      throw new Error('Period is required');
    }

    if (!payload.reason) {
      throw new Error('Reason is required');
    }

    loadingUserLimits.value = true;

    const { updateWallet } = useWallet();

    const result = await ApiService.patch('/bff/users/timeout-limits', payload)
      .then(async (res: any) => {
        await fetchUserProfile({ force: true });
        await updateWallet();

        return res;
      })
      .catch((error: any) => {
        console.error({ error });

        throw error;
      })
      .finally(() => {
        loadingUserLimits.value = false;
      });

    return result;
  }

  async function setSelfExclusion(payload: SelfExclusionPayload) {
    if (!payload.user_exclusion) {
      throw new Error('Period is required');
    }

    if (!payload.reason) {
      throw new Error('Reason is required');
    }

    loadingUserLimits.value = true;

    const { updateWallet } = useWallet();

    const result = await ApiService.patch('/bff/users/self-exclusion', payload)
      .then(async (res: any) => {
        await fetchUserProfile({ force: true });
        await updateWallet();

        return res;
      })
      .catch((error: any) => {
        console.error({ error });

        throw error;
      })
      .finally(() => {
        loadingUserLimits.value = false;
      });

    return result;
  }

  async function setAccountLimits(payload: UserLimitPayload) {
    loadingUserLimits.value = true;

    const { updateWallet } = useWallet();

    const result = await ApiService.patch('/bff/users/update-limits', payload)
      .then(async (res: any) => {
        await fetchUserProfile({ force: true });
        await updateWallet();

        return res;
      })
      .catch((error: any) => {
        console.error({ error });

        throw error;
      })
      .finally(() => {
        loadingUserLimits.value = false;
      });

    return result;
  }

  const userTimeoutMinOptionVal = computed<string>(() => {
    return String(userLimits.value?.user_pause_min || '0');
  });

  const userIsOnTimeoutActive = computed<boolean>(() => {
    return (
      !!userLimits.value?.user_pause ||
      !!userLimits.value?.user_pause_at ||
      false
    );
  });

  const userTimeoutUntil = computed<string>(() => {
    return getDateTime(userLimits.value?.user_pause_in || '');
  });

  const userExclusionMinOptionVal = computed<string>(() => {
    return String(userLimits.value?.user_exclusion_min || '0');
  });

  const userIsOnExclusionActive = computed<boolean>(() => {
    return (
      !!userLimits.value?.user_exclusion ||
      !!userLimits.value?.user_exclusion_at ||
      false
    );
  });

  const userIsOnExclusionPermanentActive = computed<boolean>(() => {
    if (!userIsOnExclusionActive.value) {
      return false;
    }

    return userLimits.value?.user_exclusion === 99;
  });

  const userExclusionUntil = computed<string>(() => {
    return getDateTime(userLimits.value?.user_exclusion_in || '');
  });

  const isUserExceedTimeoutLimit = computed<boolean>(() => {
    return !!userExceedTimeoutLimit.value || false;
  });

  const userIsOnExclusionOperatorActive = computed<boolean>(() => {
    return (
      !!userLimits.value?.operator_exclusion ||
      !!userLimits.value?.operator_exclusion_at ||
      false
    );
  });

  const userExclusionOperatorUntil = computed<string>(() => {
    return getDateTime(userLimits.value?.operator_exclusion_at || '');
  });

  const userExclusionOperatorNote = computed<string>(() => {
    return userLimits.value?.operator_exclusion_note || '';
  });

  const userIsOnExclusionSpaActive = computed<boolean>(() => {
    return (
      !!userLimits.value?.spa_exclusion && !!userLimits.value?.spa_exclusion_at
    );
  });

  const userExclusionSpaPeriod = computed<number>(() => {
    return userLimits.value?.spa_exclusion || 0;
  });

  const userExclusionSpaUntil = computed<string>(() => {
    return getDateTime(userLimits.value?.spa_exclusion_at || '');
  });

  const userExclusionSpaNote = computed<string>(() => {
    return userLimits.value?.spa_exclusion_note || '';
  });

  const userCanOnlyWithdraw = computed<boolean>(() => {
    const { isWithinMigrationPeriod } = useFeatureFlagsNew();

    if (isWithinMigrationPeriod.value) {
      return true;
    }

    if (!isAuthenticated.value) {
      return false;
    }

    return (
      userIsOnExclusionActive.value ||
      userIsOnTimeoutActive.value ||
      isUserExceedTimeoutLimit.value ||
      userIsOnExclusionOperatorActive.value ||
      userIsOnExclusionSpaActive.value ||
      isWithinMigrationPeriod.value
    );
  });

  return {
    userLimits,
    setTimeOutLimit,
    setSelfExclusion,
    setAccountLimits,
    userTimeoutMinOptionVal,
    userIsOnTimeoutActive,
    userTimeoutUntil,
    userExclusionMinOptionVal,
    userIsOnExclusionActive,
    userIsOnExclusionPermanentActive,
    userExclusionUntil,
    isUserExceedTimeoutLimit,
    userIsOnExclusionOperatorActive,
    userExclusionOperatorUntil,
    userExclusionOperatorNote,
    userIsOnExclusionSpaActive,
    userExclusionSpaPeriod,
    userExclusionSpaUntil,
    userExclusionSpaNote,
    userCanOnlyWithdraw,
  };
};
